import { create } from 'zustand';
import { 
  getAllReadinessTemplates, 
  createReadinessTemplate, 
  updateReadinessTemplate, 
  deleteReadinessTemplate,
  createReadinessInspection,
  getReadinessInspectionHistory
} from '../service/api';
import { toast } from 'react-toastify';

interface ReadinessTemplate {
  id?: string;
  _id?: string;
  emergencyCategoryId: string;
  emergencySubcategoryId: string;
  title: string;
  version?: number;
  checklistConfig: ChecklistItem[];
  inspectionFrequencyHours: number;
  isActive?: boolean;
}

interface ChecklistItem {
  controlId: string;
  prompt: string;
  type: 'BOOLEAN' | 'NUMERIC' | 'PHOTO_PROOF';
  isCritical: boolean;
  minValue?: number;
  maxValue?: number;
  unit?: string;
}

interface ReadinessState {
  templates: ReadinessTemplate[];
  loading: boolean;
  error: string | null;
  
  fetchTemplates: () => Promise<void>;
  saveTemplate: (template: ReadinessTemplate) => Promise<boolean>;
  removeTemplate: (id: string) => Promise<boolean>;
  
  performInspection: (inspectionData: any) => Promise<boolean>;
  fetchInspectionHistory: (equipmentId: string) => Promise<any[]>;
}

export const useReadinessStore = create<ReadinessState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,

  fetchTemplates: async () => {
    set({ loading: true, error: null });
    try {
      const response = await getAllReadinessTemplates();
      if (response.data && response.data.success) {
        set({ templates: response.data.data, loading: false });
      } else {
        set({ error: 'Failed to fetch templates', loading: false });
      }
    } catch (err: any) {
      set({ error: err.message, loading: false });
      toast.error('Error fetching readiness templates');
    }
  },

  saveTemplate: async (template: ReadinessTemplate) => {
    set({ loading: true });
    try {
      let response;
      if (template.id || template._id) {
        response = await updateReadinessTemplate((template.id || template._id) as string, template);
        toast.success('Template updated successfully');
      } else {
        response = await createReadinessTemplate(template);
        toast.success('Template created successfully');
      }
      
      if (response.data && response.data.success) {
        await get().fetchTemplates();
        set({ loading: false });
        return true;
      }
      set({ loading: false });
      return false;
    } catch (err: any) {
      set({ loading: false });
      toast.error(err.message || 'Error saving template');
      return false;
    }
  },

  removeTemplate: async (id: string) => {
    set({ loading: true });
    try {
      const response = await deleteReadinessTemplate(id);
      if (response.data && response.data.success) {
        toast.success('Template deleted successfully');
        await get().fetchTemplates();
        set({ loading: false });
        return true;
      }
      set({ loading: false });
      return false;
    } catch (err: any) {
      set({ loading: false });
      toast.error(err.message || 'Error deleting template');
      return false;
    }
  },

  performInspection: async (inspectionData: any) => {
    set({ loading: true });
    try {
      const response = await createReadinessInspection(inspectionData);
      if (response.data && response.data.success) {
        toast.success('Inspection submitted successfully');
        set({ loading: false });
        return true;
      }
      set({ loading: false });
      return false;
    } catch (err: any) {
      set({ loading: false });
      toast.error(err.message || 'Error submitting inspection');
      return false;
    }
  },

  fetchInspectionHistory: async (equipmentId: string) => {
    try {
      const response = await getReadinessInspectionHistory(equipmentId);
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err: any) {
      toast.error('Error fetching inspection history');
      return [];
    }
  }
}));
